import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Filter, Loader2, Pencil, TrendingUp, Upload, X } from "lucide-react";
import { api } from "../api";
import { Card } from "../ui";
import { DateInput } from "../DateInput";
import { formatCpfCnpj } from "../../lib/masks";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pago", label: "Pago" },
  { value: "em_aberto", label: "Em aberto" },
  { value: "vencido", label: "Vencido" },
];
const TIPO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "Mensalidade", label: "Mensalidade" },
  { value: "Anuidade", label: "Anuidade" },
  { value: "Taxa Extra", label: "Taxa Extra" },
  { value: "Multa", label: "Multa" },
  { value: "Outro", label: "Outro" },
];

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(v) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};

export default function AdminRelatorios() {
  const [associados, setAssociados] = useState([]);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    associate_id: "",
    min_value: "",
    max_value: "",
    status: "",
    boleto_type: "",
  });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, total_valor: 0, total_pago: 0, total_em_aberto: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [editing, setEditing] = useState(null); // row being edited (null = closed)

  const importXlsx = async (fileInput) => {
    const f = fileInput.target.files?.[0];
    fileInput.target.value = ""; // allow re-uploading same file
    if (!f) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/admin/reports/payments/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(res.data);
      const c = res.data.counts || {};
      if (c.updated > 0) {
        toast.success(
          `${c.updated} boleto(s) atualizado(s)` +
            (c.not_found ? ` · ${c.not_found} não encontrado(s)` : "")
        );
      } else if (c.not_found > 0) {
        toast.warning(`Nenhum boleto atualizado — ${c.not_found} não encontrado(s).`);
      } else {
        toast.info("Nenhuma linha para atualizar.");
      }
      run();
    } catch {
      toast.error("Falha ao importar planilha.");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    api
      .get("/admin/associados")
      .then((r) => setAssociados(r.data))
      .catch((e) => console.warn("Falha ao carregar associados:", e));
  }, []);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    return p;
  }, [filters]);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reports/payments", { params });
      setRows(res.data.rows || []);
      setTotals(res.data.totals || { count: 0, total_valor: 0, total_pago: 0, total_em_aberto: 0 });
    } catch {
      toast.error("Falha ao carregar relatório.");
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/reports/payments/export", {
        params,
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `pagamentos.xlsx`;
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado: ${filename}`);
    } catch {
      toast.error("Falha ao exportar XLSX.");
    } finally {
      setExporting(false);
    }
  };

  // Initial load only; later runs are triggered by the "Filtrar" button.
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    runRef.current();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Financeiro</div>
          <h1 className="mt-1 font-serif-display text-3xl">Relatório de pagamentos</h1>
          <p className="mt-1 text-sm text-slate-600">
            Filtre por período, associado, valor, status e tipo. Exporte para Excel a
            qualquer momento.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label
            className={`inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.25em] hover:border-[#002776] hover:text-[#002776] cursor-pointer transition-colors ${
              importing ? "opacity-60 pointer-events-none" : ""
            }`}
            data-testid="report-import-label"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Importar XLSX
            <input
              data-testid="report-import-input"
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={importXlsx}
              disabled={importing}
            />
          </label>
          <button
            data-testid="report-export-btn"
            onClick={exportXlsx}
            disabled={exporting || rows.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#002776] text-[#002776] text-[11px] uppercase tracking-[0.25em] hover:bg-[#002776] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Exportar XLSX
          </button>
        </div>
      </div>

      <Card className="mt-6 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={14} className="text-slate-500" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Filtros</span>
        </div>        <div className="grid md:grid-cols-4 gap-3">
          <FieldWrap label="Data inicial">
            <DateInput
              testid="filter-start-date"
              value={filters.start_date}
              onChange={(v) => setFilters({ ...filters, start_date: v })}
            />
          </FieldWrap>
          <FieldWrap label="Data final">
            <DateInput
              testid="filter-end-date"
              value={filters.end_date}
              onChange={(v) => setFilters({ ...filters, end_date: v })}
            />
          </FieldWrap>
          <FieldWrap label="Associado">
            <select
              data-testid="filter-associate"
              value={filters.associate_id}
              onChange={(e) => setFilters({ ...filters, associate_id: e.target.value })}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm bg-white"
            >
              <option value="">Todos</option>
              {associados.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </FieldWrap>
          <FieldWrap label="Status">
            <select
              data-testid="filter-status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm bg-white"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FieldWrap>
          <FieldWrap label="Valor mínimo (R$)">
            <input
              data-testid="filter-min-value"
              type="number"
              step="0.01"
              value={filters.min_value}
              onChange={(e) => setFilters({ ...filters, min_value: e.target.value })}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm"
            />
          </FieldWrap>
          <FieldWrap label="Valor máximo (R$)">
            <input
              data-testid="filter-max-value"
              type="number"
              step="0.01"
              value={filters.max_value}
              onChange={(e) => setFilters({ ...filters, max_value: e.target.value })}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm"
            />
          </FieldWrap>
          <FieldWrap label="Tipo">
            <select
              data-testid="filter-tipo"
              value={filters.boleto_type}
              onChange={(e) => setFilters({ ...filters, boleto_type: e.target.value })}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm bg-white"
            >
              {TIPO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FieldWrap>
          <div className="flex items-end">
            <button
              data-testid="report-apply-btn"
              onClick={run}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a] transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Filter size={14} />}
              Aplicar
            </button>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-4 gap-3 mt-6" data-testid="report-totals">        <StatCard label="Boletos no filtro" value={totals.count} />
        <StatCard label="Valor total" value={fmtBRL(totals.total_valor)} />
        <StatCard label="Total pago" value={fmtBRL(totals.total_pago)} color="text-emerald-700" />
        <StatCard label="Total em aberto" value={fmtBRL(totals.total_em_aberto)} color="text-amber-700" />
      </div>

      {importReport && (
        <Card
          data-testid="import-report-card"
          className="mt-4 p-4 border-l-4 border-l-[#002776]"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm">
              <strong className="text-[#002776]">Importação concluída:</strong>{" "}
              {importReport.counts?.updated || 0} atualizado(s)
              {importReport.counts?.not_found
                ? ` · ${importReport.counts.not_found} não encontrado(s)`
                : ""}
              {importReport.counts?.skipped
                ? ` · ${importReport.counts.skipped} sem alterações`
                : ""}
              {importReport.counts?.errors
                ? ` · ${importReport.counts.errors} erro(s)`
                : ""}
            </div>
            <button
              onClick={() => setImportReport(null)}
              className="text-xs text-slate-500 hover:text-slate-800"
            >
              fechar
            </button>
          </div>
          {(importReport.not_found?.length || 0) > 0 && (
            <details className="mt-2 text-xs text-slate-600">
              <summary className="cursor-pointer">
                Ver {importReport.not_found.length} não encontrados
              </summary>
              <ul className="mt-1 ml-4 list-disc">
                {importReport.not_found.slice(0, 30).map((n) => (
                  <li key={n.row}>
                    Linha {n.row}: Nº <strong>{n.numero_boleto}</strong>
                  </li>
                ))}
                {importReport.not_found.length > 30 && (
                  <li>(+{importReport.not_found.length - 30} outros)</li>
                )}
              </ul>
            </details>
          )}
        </Card>
      )}

      <Card className="mt-4 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Nº Boleto</th>
                <th className="text-left px-3 py-2">Associado</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-right px-3 py-2">Valor</th>
                <th className="text-left px-3 py-2">Vencimento</th>
                <th className="text-left px-3 py-2">Upload</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Pago em</th>
                <th className="text-left px-3 py-2">Nº Recibo</th>
                <th className="text-right px-3 py-2 sticky right-0 bg-slate-50">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="report-rows">
              {loading && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                    <TrendingUp size={16} className="inline mr-1" />
                    Nenhum pagamento encontrado com os filtros aplicados.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`report-row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-3 py-2 font-mono text-xs">{r.numero_boleto || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.associate_name || "—"}</div>
                    <div className="text-xs text-slate-500">
                      {r.associate_document ? formatCpfCnpj(r.associate_document) : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.boleto_type || "—"}</td>
                  <td className="px-3 py-2 text-right font-medium">{r.valor ? fmtBRL(r.valor) : "—"}</td>
                  <td className="px-3 py-2">{fmtDate(r.due_date)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {fmtDate((r.uploaded_at || "").slice(0, 10))}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wide ${
                        r.payment_status === "pago"
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                          : "bg-amber-50 text-amber-800 border border-amber-300"
                      }`}
                    >
                      {r.payment_status === "pago" ? "Pago" : "Em aberto"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {fmtDate((r.paid_at || "").slice(0, 10))}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.receipt_number || "—"}</td>
                  <td className="px-3 py-2 text-right sticky right-0 bg-white">
                    <button
                      data-testid={`report-edit-btn-${r.id}`}
                      onClick={() => setEditing(r)}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wide text-slate-700 hover:border-[#002776] hover:text-[#002776] transition-colors"
                      title="Editar boleto"
                    >
                      <Pencil size={12} />
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <EditBoletoModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            run();
          }}
        />
      )}
    </div>
  );
}

const FieldWrap = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</label>
    {children}
  </div>
);

const StatCard = ({ label, value, color = "text-slate-900" }) => (
  <div className="border border-slate-200 rounded-sm p-4 bg-white">
    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
    <div className={`mt-1 font-serif-display text-2xl ${color}`}>{value}</div>
  </div>
);

function EditBoletoModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    numero_boleto: row.numero_boleto || "",
    valor: row.valor != null ? String(row.valor) : "",
    due_date: (row.due_date || "").slice(0, 10),
    boleto_type: row.boleto_type || "",
    payment_status: row.payment_status || "em_aberto",
    paid_at: (row.paid_at || "").slice(0, 10),
    receipt_number: row.receipt_number || "",
    notes: row.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        numero_boleto: form.numero_boleto.trim(),
        valor: form.valor === "" ? null : Number(form.valor),
        due_date: form.due_date || "",
        boleto_type: form.boleto_type,
        payment_status: form.payment_status,
        paid_at:
          form.payment_status === "pago" ? form.paid_at || "" : "",
        receipt_number: form.receipt_number.trim(),
        notes: form.notes,
      };
      // Remove `valor:null` (don't send if empty — treat as unchanged)
      if (payload.valor === null) delete payload.valor;
      await api.patch(`/admin/files/${row.id}`, payload);
      toast.success("Boleto atualizado.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-boleto-modal"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Editar boleto manualmente
            </div>
            <h3 className="mt-0.5 font-serif-display text-xl">
              {row.associate_name || "—"}
              {row.numero_boleto ? (
                <span className="text-slate-500 text-sm font-sans ml-2">
                  · Nº {row.numero_boleto}
                </span>
              ) : null}
            </h3>
          </div>
          <button
            data-testid="edit-boleto-close"
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-sm text-slate-500"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-4">
          <FieldWrap label="Nº do boleto">
            <input
              data-testid="edit-numero"
              value={form.numero_boleto}
              onChange={(e) => set("numero_boleto", e.target.value)}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm"
            />
          </FieldWrap>
          <FieldWrap label="Valor (R$)">
            <input
              data-testid="edit-valor"
              type="number"
              step="0.01"
              value={form.valor}
              onChange={(e) => set("valor", e.target.value)}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm"
            />
          </FieldWrap>
          <FieldWrap label="Vencimento">
            <DateInput
              testid="edit-due-date"
              value={form.due_date}
              onChange={(v) => set("due_date", v)}
            />
          </FieldWrap>
          <FieldWrap label="Tipo">
            <select
              data-testid="edit-tipo"
              value={form.boleto_type}
              onChange={(e) => set("boleto_type", e.target.value)}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm bg-white"
            >
              <option value="">—</option>
              <option value="Mensalidade">Mensalidade</option>
              <option value="Anuidade">Anuidade</option>
              <option value="Taxa Extra">Taxa Extra</option>
              <option value="Multa">Multa</option>
              <option value="Outro">Outro</option>
            </select>
          </FieldWrap>

          <FieldWrap label="Status de pagamento">
            <select
              data-testid="edit-status"
              value={form.payment_status}
              onChange={(e) => set("payment_status", e.target.value)}
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm bg-white"
            >
              <option value="em_aberto">Em aberto</option>
              <option value="pago">Pago</option>
            </select>
          </FieldWrap>
          <FieldWrap label="Pago em">
            <DateInput
              testid="edit-paid-at"
              value={form.paid_at}
              disabled={form.payment_status !== "pago"}
              onChange={(v) => set("paid_at", v)}
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Se ficar vazio ao marcar como pago, será usada a data de hoje.
            </p>
          </FieldWrap>

          <FieldWrap label="Nº Recibo (opcional)">
            <input
              data-testid="edit-receipt-number"
              value={form.receipt_number}
              onChange={(e) => set("receipt_number", e.target.value)}
              placeholder="Preencher só se recibo foi emitido fora do sistema"
              className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm"
            />
          </FieldWrap>
          <div className="md:col-span-2">
            <FieldWrap label="Observações">
              <textarea
                data-testid="edit-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Informações adicionais sobre este boleto"
                className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm resize-y"
              />
            </FieldWrap>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3 sticky bottom-0 bg-white">
          <p className="text-[11px] text-slate-500">
            A edição manual não gera recibo automaticamente. Para emitir o PDF use o botão
            &quot;Gerar recibo&quot; em Arquivos.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.25em] hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              data-testid="edit-boleto-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a] transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Salvar alterações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
